import { jsPDF } from 'jspdf';
// html2canvas-proを使用: Tailwind CSS v4のOKLCHカラー形式サポートのため
// 標準のhtml2canvasはOKLCH/LCH/LAB等の新しいCSS色空間をサポートしていない
import html2canvas from 'html2canvas-pro';
import logger from './logger';

export interface PDFOptions {
  filename?: string;
  orientation?: 'portrait' | 'landscape';
  format?: 'a4' | 'a3' | 'letter';
  margin?: number;
  scale?: number;
}

export interface PDFGenerationResult {
  success: boolean;
  error?: string;
  filename?: string;
}

/**
 * HTMLエリアをPDFに変換してダウンロードする
 */
export async function generateCasePDF(
  element: HTMLElement,
  viewMode: 'panel' | 'table',
  options: PDFOptions = {}
): Promise<PDFGenerationResult> {
  try {
    logger.info('PDF生成を開始', { viewMode, options });

    const {
      filename = generateFilename(),
      orientation = 'portrait',
      format = 'a4',
      margin = 15,
      scale = 2,
    } = options;

    // 印刷用のクラスを一時的に追加
    const originalClasses = element.className;
    element.classList.add('print-mode');

    // ボタンやナビゲーション要素を一時的に非表示
    const printElements = addPrintHeader(element);

    try {
      // html2canvasでキャプチャ
      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        width: element.scrollWidth,
        height: element.scrollHeight,
        onclone: clonedDoc => {
          // クローン要素での追加調整
          const clonedElement = clonedDoc.querySelector('[data-print-area]') as HTMLElement;
          if (clonedElement) {
            // ダークモードクラスを除去（印刷用に強制ライトモード）
            clonedElement.classList.remove('dark');
          }
        },
      });

      // PDF生成
      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format,
      });

      // A4サイズの設定
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const marginMM = margin;
      const contentWidth = pdfWidth - marginMM * 2;
      const contentHeight = pdfHeight - marginMM * 2;

      // キャンバスをPDFサイズに合わせて調整
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const ratio = Math.min(
        contentWidth / (canvasWidth * 0.264583),
        contentHeight / (canvasHeight * 0.264583)
      );

      const imgWidth = canvasWidth * 0.264583 * ratio;
      const imgHeight = canvasHeight * 0.264583 * ratio;

      // キャンバスをDataURLに変換
      const imgData = canvas.toDataURL('image/png', 1.0);

      // PDFにページを追加して画像を配置
      const pageHeight = contentHeight;

      if (imgHeight <= pageHeight) {
        // 1ページに収まる場合
        pdf.addImage(imgData, 'PNG', marginMM, marginMM, imgWidth, imgHeight);
      } else {
        // 複数ページに分割
        const pages = Math.ceil(imgHeight / pageHeight);

        for (let i = 0; i < pages; i++) {
          if (i > 0) {
            pdf.addPage();
          }

          const sourceY = i * pageHeight * (canvasHeight / imgHeight);
          const sourceHeight = Math.min(
            pageHeight * (canvasHeight / imgHeight),
            canvasHeight - sourceY
          );

          // ページごとのキャンバスを作成
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvasWidth;
          pageCanvas.height = sourceHeight;

          const pageCtx = pageCanvas.getContext('2d');
          if (pageCtx) {
            pageCtx.drawImage(
              canvas,
              0,
              sourceY,
              canvasWidth,
              sourceHeight,
              0,
              0,
              canvasWidth,
              sourceHeight
            );

            const pageImgData = pageCanvas.toDataURL('image/png', 1.0);
            const pageImgHeight = sourceHeight * 0.264583 * ratio;

            pdf.addImage(pageImgData, 'PNG', marginMM, marginMM, imgWidth, pageImgHeight);
          }
        }
      }

      // PDFをダウンロード
      pdf.save(filename);

      logger.info('PDF生成完了', { filename, pages: pdf.getNumberOfPages() });

      return {
        success: true,
        filename,
      };
    } finally {
      // 元のクラスを復元
      element.className = originalClasses;
      // 印刷用要素を削除
      removePrintElements(printElements);
    }
  } catch (error) {
    logger.error('PDF生成エラー', error);

    return {
      success: false,
      error: error instanceof Error ? error.message : '不明なエラーが発生しました',
    };
  }
}

/**
 * PDFファイル名を自動生成
 */
function generateFilename(): string {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10); // YYYY-MM-DD
  const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, ''); // HHMMSS
  return `cases_${dateStr}_${timeStr}.pdf`;
}

/**
 * 印刷用ヘッダーを追加
 */
function addPrintHeader(element: HTMLElement): HTMLElement[] {
  const printElements: HTMLElement[] = [];

  // 印刷用ヘッダーを作成
  const header = document.createElement('div');
  header.className = 'print-header print-only';
  header.innerHTML = `
    <h1>案件一覧</h1>
    <div class="print-date">出力日時: ${new Date().toLocaleString('ja-JP')}</div>
  `;

  element.insertBefore(header, element.firstChild);
  printElements.push(header);

  // 印刷用フッターを作成
  const footer = document.createElement('div');
  footer.className = 'print-footer print-only';
  footer.innerHTML = `
    <div>株式会社サンプル陸送 - 案件一覧出力</div>
  `;

  element.appendChild(footer);
  printElements.push(footer);

  // data-print-area 属性を追加
  element.setAttribute('data-print-area', 'true');

  return printElements;
}

/**
 * 印刷用要素を削除
 */
function removePrintElements(elements: HTMLElement[]): void {
  elements.forEach(element => {
    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }
  });
}

/**
 * 印刷プレビュー用のHTML文字列を生成
 */
export function generatePrintPreviewHTML(element: HTMLElement): string {
  const clonedElement = element.cloneNode(true) as HTMLElement;

  // 印刷用要素を追加
  addPrintHeader(clonedElement);

  // スタイルシートのリンクを取得
  const stylesheets = Array.from(document.styleSheets)
    .map(sheet => {
      try {
        if (sheet.href) {
          return `<link rel="stylesheet" href="${sheet.href}">`;
        }
        return '';
      } catch {
        return '';
      }
    })
    .join('\n');

  const html = `
    <!DOCTYPE html>
    <html lang="ja">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>印刷プレビュー - 案件一覧</title>
      ${stylesheets}
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        @media screen { body { padding: 20px; background: #f5f5f5; } }
      </style>
    </head>
    <body>
      ${clonedElement.outerHTML}
    </body>
    </html>
  `;

  return html;
}
